/**
 * Fetches and parses `stations.json` and per-station `manifest.json` for panorama-bubble-v1.
 */

import {
  PanoramaBubbleStationsManifestSchema,
  PanoramaBubbleStationManifestSchema,
  MinimapDescriptorSchema,
  type PanoramaBubbleStationsManifest,
  type PanoramaBubbleStationManifest,
} from '@geosoft/converter-viewer-handoff';
import { resolveRelativeUrl } from './urlUtils';

const FETCH_TIMEOUT_MS = 30_000;

// ── Minimap descriptor ─────────────────────────────────────────────────────────
//
// `minimap.json` schemas now live in the shared contract package
// (`@geosoft/converter-viewer-handoff`, RFC-120 §2) — floorplan / osm / xyz /
// pmtiles. Re-exported here so engine-internal callers keep a single import
// point and stay decoupled from the package layout.

export {
  MinimapDescriptorSchema,
  FloorplanMinimapDescriptorSchema,
  OsmMinimapDescriptorSchema,
  XyzMinimapDescriptorSchema,
  PmtilesMinimapDescriptorSchema,
} from '@geosoft/converter-viewer-handoff';
export type {
  MinimapDescriptor,
  FloorplanMinimapDescriptor,
  OsmMinimapDescriptor,
  XyzMinimapDescriptor,
  PmtilesMinimapDescriptor,
} from '@geosoft/converter-viewer-handoff';
import type { MinimapDescriptor } from '@geosoft/converter-viewer-handoff';

export async function loadStations(
  url: string,
  signal?: AbortSignal,
): Promise<PanoramaBubbleStationsManifest> {
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS, signal);
  if (!res.ok) throw new ManifestLoadError(`stations fetch ${res.status}: ${url}`);
  const json: unknown = await res.json();
  const parsed = PanoramaBubbleStationsManifestSchema.safeParse(json);
  if (!parsed.success) {
    throw new ManifestSchemaError('stations.json', formatZodError(parsed.error));
  }
  return parsed.data;
}

export async function loadStationManifest(
  url: string,
  signal?: AbortSignal,
): Promise<PanoramaBubbleStationManifest> {
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS, signal);
  if (!res.ok) throw new ManifestLoadError(`station manifest fetch ${res.status}: ${url}`);
  const json: unknown = await res.json();
  const parsed = PanoramaBubbleStationManifestSchema.safeParse(json);
  if (!parsed.success) {
    throw new ManifestSchemaError('manifest.json', formatZodError(parsed.error));
  }
  return parsed.data;
}

/**
 * Fetch + validate the optional `minimap.json` descriptor sitting alongside
 * `stations.json` in the same bubble project. Returns `null` when the file
 * is absent (HTTP 404 / network 4xx) — legacy projects predate the
 * descriptor and the viewer must keep working without it.
 *
 * Resolves the descriptor URL relative to `stationsUrl` (the project root is
 * one directory up from any per-station artifact). Any `imageUrl` inside a
 * `floorplan` descriptor is rewritten to an absolute URL so callers can pass
 * it straight to `<img>` / `fetch` without re-doing the join.
 */
export async function loadMinimapDescriptor(
  stationsUrl: string,
  signal?: AbortSignal,
): Promise<MinimapDescriptor | null> {
  const descriptorUrl = resolveRelativeUrl(stationsUrl, 'minimap.json');
  let res: Response;
  try {
    res = await fetchWithTimeout(descriptorUrl, FETCH_TIMEOUT_MS, signal);
  } catch {
    // Network/abort errors propagate as "no descriptor" so the viewer can
    // fall through to the OSM legacy path instead of crashing.
    return null;
  }
  if (res.status === 404) return null;
  if (!res.ok) throw new ManifestLoadError(`minimap.json fetch ${res.status}: ${descriptorUrl}`);
  const json: unknown = await res.json();
  const parsed = MinimapDescriptorSchema.safeParse(json);
  if (!parsed.success) {
    throw new ManifestSchemaError('minimap.json', formatZodError(parsed.error));
  }
  const descriptor = parsed.data;
  if (descriptor.kind === 'floorplan') {
    return {
      ...descriptor,
      imageUrl: resolveRelativeUrl(descriptorUrl, descriptor.imageUrl),
    };
  }
  return descriptor;
}

export class ManifestLoadError extends Error {
  readonly url?: string;

  constructor(message: string, url?: string) {
    super(message);
    this.name = 'ManifestLoadError';
    this.url = url ?? extractUrlFromMessage(message);
  }
}

export class ManifestSchemaError extends Error {
  readonly manifestId: string;
  readonly fieldPath: string;

  constructor(manifestId: string, message: string) {
    const fieldPath = extractFieldPath(message);
    super(`Schema validation failed for ${manifestId}${fieldPath ? ` at ${fieldPath}` : ''}: ${message}`);
    this.name = 'ManifestSchemaError';
    this.manifestId = manifestId;
    this.fieldPath = fieldPath;
  }
}

function fetchWithTimeout(url: string, ms: number, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(new DOMException(`Fetch timed out after ${ms}ms`, 'TimeoutError'));
  }, ms);

  const abortFromParent = (): void => {
    controller.abort(signal?.reason);
  };

  if (signal) {
    if (signal.aborted) {
      clearTimeout(timeoutId);
      return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }
    signal.addEventListener('abort', abortFromParent, { once: true });
  }

  return fetch(url, { signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', abortFromParent);
  });
}

function formatZodError(error: { issues: ReadonlyArray<{ path: ReadonlyArray<string | number>; message: string }> }): string {
  if (error.issues.length === 0) return 'invalid manifest';
  return error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
      return `${path}: ${issue.message}`;
    })
    .join('; ');
}

function extractFieldPath(message: string): string {
  const match = /^([^:]+):/.exec(message);
  return match?.[1] ?? '';
}

function extractUrlFromMessage(message: string): string | undefined {
  const match = /:\s*(https?:\/\/\S+|\/\S+)\s*$/.exec(message);
  return match?.[1];
}
